import React from "react";
import { SearchIcon, type LucideIcon } from "lucide-react";
import { UsersSettingsTab } from "./tabs";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useT } from "@/i18n";

export type StatusOption = {
  id: UsersSettingsTab;
  label: string;
  icon: LucideIcon;
};

export type BusinessOption = {
  id: string;
  label: string;
  disabled?: boolean;
  metadata?: React.ReactNode;
};

type UserFiltersProps = {
  selectedTab: UsersSettingsTab;
  setSelectedTab: (tab: UsersSettingsTab) => void;
  statusOptions: StatusOption[];
  businessOptions: BusinessOption[];
  selectedBusinessId?: string;
  onSelectBusiness: (businessId: string) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
};

export function UserFilters({
  selectedTab,
  setSelectedTab,
  statusOptions,
  businessOptions,
  selectedBusinessId,
  onSelectBusiness,
  searchTerm,
  onSearchTermChange,
}: UserFiltersProps) {
  const selectedBusiness = businessOptions.find((b) => b.id === selectedBusinessId);
  const { t } = useT();

  return (
    <div className="flex shrink-0 flex-col gap-2 px-3 pb-3 sm:flex-row sm:flex-wrap sm:items-center md:px-4">
      <Tabs value={selectedTab} onValueChange={(tab) => setSelectedTab(tab as UsersSettingsTab)}>
        <TabsList aria-label={t("users.accountType")} className="w-full sm:w-fit">
          {statusOptions.map(({ id, label, icon: Icon }) => (
            <TabsTrigger key={id} value={id} className="px-2.5">
              <Icon aria-hidden="true" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {businessOptions.length > 1 && (
        <Select value={selectedBusinessId || ""} onValueChange={onSelectBusiness}>
          <SelectTrigger
            aria-label={t("common.business")}
            className="w-full sm:w-auto sm:max-w-64 sm:min-w-40"
          >
            {/* Show only the name: the status note is for the menu. */}
            <SelectValue placeholder={t("common.selectBusiness")}>
              <span dir="auto">{selectedBusiness?.label}</span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            {businessOptions.map((option) => (
              <SelectItem key={option.id} value={option.id} disabled={option.disabled}>
                <span dir="auto" className="truncate">
                  {option.label}
                </span>
                {option.metadata}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="relative w-full sm:ms-auto sm:w-64">
        <SearchIcon
          aria-hidden="true"
          className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          placeholder={t("common.search")}
          aria-label={t("common.search")}
          className="ps-8"
        />
      </div>
    </div>
  );
}
